import { useState, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Search, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import type { DataSourceField } from '@/lib/api/types'
import { cn } from '@/lib/utils'

interface DataSourceFieldsPanelProps {
  fields: DataSourceField[]
  loading: boolean
  onFieldSelect?: (field: DataSourceField) => void
  selectedFieldId?: string
}

interface FieldGroup {
  name: string
  fields: DataSourceField[]
}

const dataTypeColors: Record<string, string> = {
  string: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  number: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  boolean: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  date: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  datetime: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  email: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
  url: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  phone: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
}

export function DataSourceFieldsPanel({
  fields,
  loading,
  onFieldSelect,
  selectedFieldId,
}: DataSourceFieldsPanelProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  // Group fields by groupName and filter by search
  const groupedFields = useMemo(() => {
    const filtered = fields.filter(
      (field) =>
        field.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        field.fieldKey.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (field.description && field.description.toLowerCase().includes(searchQuery.toLowerCase()))
    )

    const groups: FieldGroup[] = []
    const ungrouped: DataSourceField[] = []

    filtered.forEach((field) => {
      if (field.groupName) {
        let group = groups.find((g) => g.name === field.groupName)
        if (!group) {
          group = { name: field.groupName, fields: [] }
          groups.push(group)
        }
        group.fields.push(field)
      } else {
        ungrouped.push(field)
      }
    })

    // Sort groups by name
    groups.sort((a, b) => a.name.localeCompare(b.name))

    // Sort fields within each group by sortOrder
    groups.forEach((group) => {
      group.fields.sort((a, b) => a.sortOrder - b.sortOrder)
    })
    ungrouped.sort((a, b) => a.sortOrder - b.sortOrder)

    return { groups, ungrouped }
  }, [fields, searchQuery])

  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupName)) {
        next.delete(groupName)
      } else {
        next.add(groupName)
      }
      return next
    })
  }

  const getDataTypeColor = (dataType: string) => {
    const lowerType = dataType.toLowerCase()
    return dataTypeColors[lowerType] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading fields...</p>
      </div>
    )
  }

  if (fields.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-2">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">No fields available</p>
      </div>
    )
  }

  const renderField = (field: DataSourceField) => (
    <button
      key={field.id}
      type="button"
      onClick={() => onFieldSelect?.(field)}
      className={cn(
        'w-full text-left px-3 py-2 rounded-md transition-colors',
        'hover:bg-neutral-100 dark:hover:bg-neutral-800',
        selectedFieldId === field.id && 'bg-neutral-100 dark:bg-neutral-800 ring-1 ring-neutral-300 dark:ring-neutral-600'
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-neutral-900 dark:text-neutral-100 truncate">
            {field.displayName}
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate font-mono">
            {field.fieldKey}
          </p>
        </div>
        <Badge className={cn('text-xs shrink-0', getDataTypeColor(field.dataType))}>
          {field.dataType}
        </Badge>
      </div>
      {field.description && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 line-clamp-2">
          {field.description}
        </p>
      )}
    </button>
  )

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
        <Input
          placeholder="Search fields..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {groupedFields.groups.map((group) => {
          const isExpanded = expandedGroups.has(group.name)
          return (
            <div key={group.name} className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleGroup(group.name)}
                className={cn(
                  'w-full flex items-center justify-between px-3 py-2 text-left',
                  'bg-neutral-50 dark:bg-neutral-800/50 hover:bg-neutral-100 dark:hover:bg-neutral-800',
                  'transition-colors'
                )}
              >
                <span className="font-medium text-sm text-neutral-900 dark:text-neutral-100">
                  {group.name}
                </span>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    {group.fields.length}
                  </Badge>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-neutral-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-neutral-400" />
                  )}
                </div>
              </button>
              {isExpanded && (
                <div className="p-2 space-y-1 border-t border-neutral-200 dark:border-neutral-700">
                  {group.fields.map(renderField)}
                </div>
              )}
            </div>
          )
        })}

        {groupedFields.ungrouped.length > 0 && (
          <div className="space-y-1 pt-2">
            {groupedFields.groups.length > 0 && (
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 px-1 mb-2">
                Other Fields
              </p>
            )}
            {groupedFields.ungrouped.map(renderField)}
          </div>
        )}

        {groupedFields.groups.length === 0 && groupedFields.ungrouped.length === 0 && searchQuery && (
          <div className="text-center py-4 text-sm text-neutral-500 dark:text-neutral-400">
            No fields match your search
          </div>
        )}
      </div>
    </div>
  )
}
